import { useEffect, useCallback, useMemo, FC } from 'react';
import intl from 'react-intl-universal';
import { Dropdown, FormItem } from '@getsynapse/design-system';
import { Option } from 'utils/customTypes';
import { PROJECT_PRIVACY } from 'utils/constants/projectsList';
import { useProjectTeams } from 'Pages/ProjectsListPage/hooks/useProjectTeams/useProjectTeams';

interface ProjectTeamsFilterProps {
  onSelect: (selected: string[]) => void;
  selected?: string[];
  appliedPrivacyFilters?: string[];
  updateAppliedPrivacyFilters: (privacy: string[]) => void;
}

const ProjectTeamsFilter: FC<ProjectTeamsFilterProps> = ({
  onSelect,
  selected,
  appliedPrivacyFilters,
  updateAppliedPrivacyFilters,
}) => {
  const { fetchLdTeams, ldTeams, canFetchMoreLdTeams } = useProjectTeams({});

  useEffect(() => {
    fetchLdTeams();
  }, [fetchLdTeams]);

  const options = useMemo(() => {
    return ldTeams.map((team) => ({
      label: team.name,
      value: team.id,
    }));
  }, [ldTeams]);

  const shouldUpdatePrivacy = useMemo(() => {
    if (appliedPrivacyFilters) {
      return !appliedPrivacyFilters.includes(PROJECT_PRIVACY.TEAM);
    }
    return true;
  }, [appliedPrivacyFilters]);

  const selectedTeams = useMemo(() => {
    return ldTeams
      .filter((team) => selected?.includes(team.id))
      .map((team) => ({
        label: team.name,
        value: team.id,
      }));
  }, [selected, ldTeams]);

  const handleChange = useCallback(
    (options: Option[]) => {
      const appliedPrivacy = appliedPrivacyFilters || [];
      const selectedIds = options.map((option) => option.value);
      onSelect(selectedIds);
      if (shouldUpdatePrivacy) {
        updateAppliedPrivacyFilters([...appliedPrivacy, PROJECT_PRIVACY.TEAM]);
      }
    },
    [
      appliedPrivacyFilters,
      onSelect,
      shouldUpdatePrivacy,
      updateAppliedPrivacyFilters,
    ]
  );

  return (
    <FormItem label={intl.get('PROJECTS_LIST_PAGE.TABLE.HEAD.TEAMS')}>
      <Dropdown
        options={options}
        onChange={handleChange}
        values={selectedTeams}
        enableLazyLoading={canFetchMoreLdTeams}
        loadMoreOptions={fetchLdTeams}
        multiple
        placeholder={intl.get('FILTER_GENERAL.PLACEHOLDER')}
        triggerProps={{
          'aria-label': 'project-teams-filter',
        }}
      />
    </FormItem>
  );
};

export default ProjectTeamsFilter;
